/**
 * Modifier System
 *
 * POE-style modifier system with flat, increased, more, reduced, less types.
 *
 * Calculation order:
 * 1. Base value
 * 2. Add all flat modifiers
 * 3. Multiply by (1 + sum of increased - sum of reduced)
 * 4. Multiply by each more modifier
 * 5. Multiply by each less modifier
 *
 * Example: 100 base, +20 flat, 50% increased, 20% more
 * = (100 + 20) * (1 + 0.5) * 1.2 = 216
 */

// ========================
// Modifier Types
// ========================

/**
 * How the modifier is applied:
 * - flat: Added directly to base value
 * - increased: Additive percentage bonus (all increased/reduced sum together)
 * - more: Multiplicative percentage bonus (each applied separately)
 * - reduced: Additive percentage penalty (subtracts from increased)
 * - less: Multiplicative percentage penalty
 */
export type ModifierType = 'flat' | 'increased' | 'more' | 'reduced' | 'less';

/**
 * All stat types in the game
 */
export type StatType =
  // Attributes
  | 'strength'
  | 'agility'
  | 'spirit'
  // Resources
  | 'maxHp'
  | 'maxMana'
  | 'energyShield'
  | 'hpRegen'
  | 'manaRegen'
  // Offense - General
  | 'damage'
  | 'attackDamage'
  | 'spellDamage'
  | 'dotDamage'
  // Offense - Elemental
  | 'physicalDamage'
  | 'odDamage'        // Fire
  | 'suDamage'        // Ice
  | 'gokDamage'       // Lightning
  | 'karanlikDamage'  // Chaos/Dark
  | 'elementalDamage'
  // Offense - Critical
  | 'critChance'
  | 'critDamage'
  // Offense - Speed
  | 'attackSpeed'
  | 'castSpeed'
  | 'cooldownReduction'
  // Offense - Other
  | 'accuracy'
  | 'projectileCount'
  | 'areaOfEffect'
  | 'range'
  // Defense
  | 'armor'
  | 'evasion'
  | 'blockChance'
  | 'dodgeChance'
  // Resistances
  | 'physicalResist'
  | 'odResist'        // Fire resist
  | 'suResist'        // Ice resist
  | 'gokResist'       // Lightning resist
  | 'karanlikResist'  // Chaos/Dark resist
  | 'allResist'
  // Utility
  | 'moveSpeed'
  | 'itemRarity'
  | 'itemQuantity'
  | 'expGain'
  // Leech
  | 'lifeLeech'
  | 'manaLeech'
  // Dodge
  | 'dodgeCharges'
  // Ailment
  | 'ailmentChance'    // General ailment application chance
  | 'burnChance'       // Specific burn chance
  | 'poisonChance'     // Specific poison chance
  | 'bleedChance'      // Specific bleed chance
  | 'ailmentEffect'    // Ailment base damage multiplier
  | 'ailmentDuration'  // Ailment duration multiplier
  // Minions
  | 'minionDamage'
  | 'minionLife'
  | 'minionSpeed';

/**
 * Stat categories for grouping
 */
export type StatCategory =
  | 'attribute'
  | 'resource'
  | 'offense'
  | 'defense'
  | 'resistance'
  | 'utility'
  | 'minion';

// ========================
// Modifier Interface
// ========================

export interface Modifier {
  stat: StatType;
  type: ModifierType;
  value: number;
  source?: string;  // Where this modifier came from (item, passive, skill, etc.)
}

/**
 * Legacy ItemMod compatibility
 */
export interface LegacyItemMod {
  type: string;
  value: number;
  tier: number;
}

// ========================
// Stat Metadata
// ========================

interface StatMeta {
  id: StatType;
  name: string;
  nameTr: string;
  category: StatCategory;
  isPercent: boolean;  // Display as percentage
  defaultValue: number;
  minValue?: number;
  maxValue?: number;
}

export const STAT_METADATA: Record<StatType, StatMeta> = {
  // Attributes
  strength: { id: 'strength', name: 'Strength', nameTr: 'Güç', category: 'attribute', isPercent: false, defaultValue: 0 },
  agility: { id: 'agility', name: 'Agility', nameTr: 'Çeviklik', category: 'attribute', isPercent: false, defaultValue: 0 },
  spirit: { id: 'spirit', name: 'Spirit', nameTr: 'Ruh', category: 'attribute', isPercent: false, defaultValue: 0 },

  // Resources
  maxHp: { id: 'maxHp', name: 'Maximum Life', nameTr: 'Maksimum Can', category: 'resource', isPercent: false, defaultValue: 100 },
  maxMana: { id: 'maxMana', name: 'Maximum Mana', nameTr: 'Maksimum Mana', category: 'resource', isPercent: false, defaultValue: 50 },
  energyShield: { id: 'energyShield', name: 'Energy Shield', nameTr: 'Enerji Kalkanı', category: 'resource', isPercent: false, defaultValue: 0 },
  hpRegen: { id: 'hpRegen', name: 'Life Regen', nameTr: 'Can Yenileme', category: 'resource', isPercent: false, defaultValue: 0 },
  manaRegen: { id: 'manaRegen', name: 'Mana Regen', nameTr: 'Mana Yenileme', category: 'resource', isPercent: false, defaultValue: 1 },

  // Offense - General
  damage: { id: 'damage', name: 'Damage', nameTr: 'Hasar', category: 'offense', isPercent: false, defaultValue: 0 },
  attackDamage: { id: 'attackDamage', name: 'Attack Damage', nameTr: 'Saldırı Hasarı', category: 'offense', isPercent: false, defaultValue: 0 },
  spellDamage: { id: 'spellDamage', name: 'Spell Damage', nameTr: 'Büyü Hasarı', category: 'offense', isPercent: false, defaultValue: 0 },
  dotDamage: { id: 'dotDamage', name: 'Damage Over Time', nameTr: 'Zamanlı Hasar', category: 'offense', isPercent: false, defaultValue: 0 },

  // Offense - Elemental
  physicalDamage: { id: 'physicalDamage', name: 'Physical Damage', nameTr: 'Fiziksel Hasar', category: 'offense', isPercent: false, defaultValue: 0 },
  odDamage: { id: 'odDamage', name: 'Fire Damage', nameTr: 'Od Hasarı', category: 'offense', isPercent: false, defaultValue: 0 },
  suDamage: { id: 'suDamage', name: 'Cold Damage', nameTr: 'Su Hasarı', category: 'offense', isPercent: false, defaultValue: 0 },
  gokDamage: { id: 'gokDamage', name: 'Lightning Damage', nameTr: 'Gök Hasarı', category: 'offense', isPercent: false, defaultValue: 0 },
  karanlikDamage: { id: 'karanlikDamage', name: 'Chaos Damage', nameTr: 'Karanlık Hasarı', category: 'offense', isPercent: false, defaultValue: 0 },
  elementalDamage: { id: 'elementalDamage', name: 'Elemental Damage', nameTr: 'Elementel Hasar', category: 'offense', isPercent: false, defaultValue: 0 },

  // Offense - Critical
  critChance: { id: 'critChance', name: 'Critical Chance', nameTr: 'Kritik Şansı', category: 'offense', isPercent: true, defaultValue: 5, minValue: 0, maxValue: 100 },
  critDamage: { id: 'critDamage', name: 'Critical Damage', nameTr: 'Kritik Hasarı', category: 'offense', isPercent: true, defaultValue: 150 },

  // Offense - Speed
  attackSpeed: { id: 'attackSpeed', name: 'Attack Speed', nameTr: 'Saldırı Hızı', category: 'offense', isPercent: true, defaultValue: 100 },
  castSpeed: { id: 'castSpeed', name: 'Cast Speed', nameTr: 'Büyü Hızı', category: 'offense', isPercent: true, defaultValue: 100 },
  cooldownReduction: { id: 'cooldownReduction', name: 'Cooldown Reduction', nameTr: 'Bekleme Azaltma', category: 'offense', isPercent: true, defaultValue: 0, maxValue: 75 },

  // Offense - Other
  accuracy: { id: 'accuracy', name: 'Accuracy', nameTr: 'İsabet', category: 'offense', isPercent: false, defaultValue: 100 },
  projectileCount: { id: 'projectileCount', name: 'Projectile Count', nameTr: 'Mermi Sayısı', category: 'offense', isPercent: false, defaultValue: 1 },
  areaOfEffect: { id: 'areaOfEffect', name: 'Area of Effect', nameTr: 'Etki Alanı', category: 'offense', isPercent: true, defaultValue: 100 },
  range: { id: 'range', name: 'Range', nameTr: 'Menzil', category: 'offense', isPercent: true, defaultValue: 100 },

  // Defense
  armor: { id: 'armor', name: 'Armor', nameTr: 'Zırh', category: 'defense', isPercent: false, defaultValue: 0 },
  evasion: { id: 'evasion', name: 'Evasion', nameTr: 'Kaçınma', category: 'defense', isPercent: false, defaultValue: 0 },
  blockChance: { id: 'blockChance', name: 'Block Chance', nameTr: 'Blok Şansı', category: 'defense', isPercent: true, defaultValue: 0, maxValue: 75 },
  dodgeChance: { id: 'dodgeChance', name: 'Dodge Chance', nameTr: 'Dodge Şansı', category: 'defense', isPercent: true, defaultValue: 0, maxValue: 75 },

  // Resistances
  physicalResist: { id: 'physicalResist', name: 'Physical Resistance', nameTr: 'Fiziksel Direnç', category: 'resistance', isPercent: true, defaultValue: 0, maxValue: 90 },
  odResist: { id: 'odResist', name: 'Fire Resistance', nameTr: 'Od Direnci', category: 'resistance', isPercent: true, defaultValue: 0, maxValue: 90 },
  suResist: { id: 'suResist', name: 'Cold Resistance', nameTr: 'Su Direnci', category: 'resistance', isPercent: true, defaultValue: 0, maxValue: 90 },
  gokResist: { id: 'gokResist', name: 'Lightning Resistance', nameTr: 'Gök Direnci', category: 'resistance', isPercent: true, defaultValue: 0, maxValue: 90 },
  karanlikResist: { id: 'karanlikResist', name: 'Chaos Resistance', nameTr: 'Karanlık Direnci', category: 'resistance', isPercent: true, defaultValue: 0, maxValue: 50 },
  allResist: { id: 'allResist', name: 'All Resistances', nameTr: 'Tüm Dirençler', category: 'resistance', isPercent: true, defaultValue: 0 },

  // Utility
  moveSpeed: { id: 'moveSpeed', name: 'Movement Speed', nameTr: 'Hareket Hızı', category: 'utility', isPercent: true, defaultValue: 100 },
  itemRarity: { id: 'itemRarity', name: 'Item Rarity', nameTr: 'Eşya Nadirliği', category: 'utility', isPercent: true, defaultValue: 100 },
  itemQuantity: { id: 'itemQuantity', name: 'Item Quantity', nameTr: 'Eşya Miktarı', category: 'utility', isPercent: true, defaultValue: 100 },
  expGain: { id: 'expGain', name: 'Experience Gain', nameTr: 'Tecrübe Kazancı', category: 'utility', isPercent: true, defaultValue: 100 },

  // Leech
  lifeLeech: { id: 'lifeLeech', name: 'Life Leech', nameTr: 'Can Emme', category: 'offense', isPercent: true, defaultValue: 0 },
  manaLeech: { id: 'manaLeech', name: 'Mana Leech', nameTr: 'Mana Emme', category: 'offense', isPercent: true, defaultValue: 0 },

  // Dodge
  dodgeCharges: { id: 'dodgeCharges', name: 'Dodge Charges', nameTr: 'Atlatma Hakkı', category: 'defense', isPercent: false, defaultValue: 3 },

  // Ailment
  ailmentChance: { id: 'ailmentChance', name: 'Ailment Chance', nameTr: 'Durum Etkisi Şansı', category: 'offense', isPercent: true, defaultValue: 0 },
  burnChance: { id: 'burnChance', name: 'Burn Chance', nameTr: 'Yanma Şansı', category: 'offense', isPercent: true, defaultValue: 0 },
  poisonChance: { id: 'poisonChance', name: 'Poison Chance', nameTr: 'Zehir Şansı', category: 'offense', isPercent: true, defaultValue: 0 },
  bleedChance: { id: 'bleedChance', name: 'Bleed Chance', nameTr: 'Kanama Şansı', category: 'offense', isPercent: true, defaultValue: 0 },
  ailmentEffect: { id: 'ailmentEffect', name: 'Ailment Effect', nameTr: 'Durum Etkisi Gücü', category: 'offense', isPercent: true, defaultValue: 0 },
  ailmentDuration: { id: 'ailmentDuration', name: 'Ailment Duration', nameTr: 'Durum Etkisi Süresi', category: 'offense', isPercent: true, defaultValue: 0 },

  // Minions
  minionDamage: { id: 'minionDamage', name: 'Minion Damage', nameTr: 'Köle Hasarı', category: 'minion', isPercent: true, defaultValue: 100 },
  minionLife: { id: 'minionLife', name: 'Minion Life', nameTr: 'Köle Canı', category: 'minion', isPercent: true, defaultValue: 100 },
  minionSpeed: { id: 'minionSpeed', name: 'Minion Speed', nameTr: 'Köle Hızı', category: 'minion', isPercent: true, defaultValue: 100 },
};

// ========================
// Modifier Calculation
// ========================

/**
 * Collection of modifiers for calculation
 */
export class ModifierCollection {
  private modifiers: Modifier[] = [];

  add(modifier: Modifier): void {
    this.modifiers.push(modifier);
  }

  addAll(modifiers: Modifier[]): void {
    this.modifiers.push(...modifiers);
  }

  clear(): void {
    this.modifiers = [];
  }

  getModifiers(): Modifier[] {
    return [...this.modifiers];
  }

  /**
   * Calculate the final value for a stat
   *
   * Order:
   * 1. base + sum(flat)
   * 2. * (1 + sum(increased)/100 - sum(reduced)/100)
   * 3. * product(1 + more/100)
   * 4. * product(1 - less/100)
   */
  calculate(stat: StatType, baseValue?: number): number {
    const meta = STAT_METADATA[stat];
    let base = baseValue ?? meta.defaultValue;

    // Get all modifiers for this stat
    const relevant = this.modifiers.filter(m => m.stat === stat);

    // Sum flat modifiers
    const flatSum = relevant
      .filter(m => m.type === 'flat')
      .reduce((sum, m) => sum + m.value, 0);

    // Sum increased/reduced (additive with each other)
    const increasedSum = relevant
      .filter(m => m.type === 'increased')
      .reduce((sum, m) => sum + m.value, 0);

    const reducedSum = relevant
      .filter(m => m.type === 'reduced')
      .reduce((sum, m) => sum + m.value, 0);

    // Get more/less multipliers (multiplicative with each other)
    const moreMultipliers = relevant
      .filter(m => m.type === 'more')
      .map(m => 1 + m.value / 100);

    const lessMultipliers = relevant
      .filter(m => m.type === 'less')
      .map(m => 1 - m.value / 100);

    // Calculate final value
    let result = base + flatSum;
    result = result * (1 + (increasedSum - reducedSum) / 100);

    for (const mult of moreMultipliers) {
      result *= mult;
    }

    for (const mult of lessMultipliers) {
      result *= mult;
    }

    // Apply min/max bounds
    if (meta.minValue !== undefined) {
      result = Math.max(meta.minValue, result);
    }
    if (meta.maxValue !== undefined) {
      result = Math.min(meta.maxValue, result);
    }

    return result;
  }

  /**
   * Get a breakdown of modifiers for a stat (for tooltips)
   */
  getBreakdown(stat: StatType): {
    flat: number;
    increased: number;
    reduced: number;
    more: number[];
    less: number[];
  } {
    const relevant = this.modifiers.filter(m => m.stat === stat);

    return {
      flat: relevant.filter(m => m.type === 'flat').reduce((sum, m) => sum + m.value, 0),
      increased: relevant.filter(m => m.type === 'increased').reduce((sum, m) => sum + m.value, 0),
      reduced: relevant.filter(m => m.type === 'reduced').reduce((sum, m) => sum + m.value, 0),
      more: relevant.filter(m => m.type === 'more').map(m => m.value),
      less: relevant.filter(m => m.type === 'less').map(m => m.value),
    };
  }
}

// ========================
// Helper Functions
// ========================

/**
 * Convert legacy ItemMod to new Modifier format
 * Legacy mods are assumed to be 'flat' type
 */
export function convertLegacyMod(legacyMod: LegacyItemMod, source?: string): Modifier {
  return {
    stat: legacyMod.type as StatType,
    type: 'flat',
    value: legacyMod.value,
    source,
  };
}

/**
 * Create a modifier
 */
export function createModifier(
  stat: StatType,
  type: ModifierType,
  value: number,
  source?: string
): Modifier {
  return { stat, type, value, source };
}

/**
 * Format modifier for display
 */
export function formatModifier(mod: Modifier): string {
  const meta = STAT_METADATA[mod.stat];
  const sign = mod.value >= 0 ? '+' : '';

  switch (mod.type) {
    case 'flat':
      return `${sign}${mod.value} ${meta.nameTr}`;
    case 'increased':
      return `${sign}${mod.value}% increased ${meta.nameTr}`;
    case 'reduced':
      return `${mod.value}% reduced ${meta.nameTr}`;
    case 'more':
      return `${sign}${mod.value}% more ${meta.nameTr}`;
    case 'less':
      return `${mod.value}% less ${meta.nameTr}`;
  }
}

/**
 * Format modifier in Turkish
 */
export function formatModifierTr(mod: Modifier): string {
  const meta = STAT_METADATA[mod.stat];
  const sign = mod.value >= 0 ? '+' : '';

  switch (mod.type) {
    case 'flat':
      return `${sign}${mod.value} ${meta.nameTr}`;
    case 'increased':
      return `%${mod.value} artırılmış ${meta.nameTr}`;
    case 'reduced':
      return `%${mod.value} azaltılmış ${meta.nameTr}`;
    case 'more':
      return `%${mod.value} daha fazla ${meta.nameTr}`;
    case 'less':
      return `%${mod.value} daha az ${meta.nameTr}`;
  }
}

// ========================
// Exports
// ========================

export default ModifierCollection;
